import React, { useState } from 'react';
import { MemberIndexEntry, MemberRole, PresenceMonth, PresenceNow } from '../types/types';
import { useBreakpoint } from '../useBreakpoint';
import Grid from '../layout/Grid';
import TwoPane from '../layout/TwoPane';
import { buttonReset, color, font, radius, space, tone, weight } from '../theme';
import TabBar from '../components/TabBar';
import { Card } from '../components/Card';
import { Badge } from '../components/Badge';
import Bar, { SegmentBar } from '../components/Bar';
import ListRow from '../components/ListRow';
import Avatar from '../components/Avatar';
import Button from '../components/Button';
import Chip from '../components/Chip';
import Icon from '../components/Icon';
import RoleTag from '../components/RoleTag';
import MetricsCard from '../components/MetricsCard';
import { ResultBar } from '../components/SelectMenu';
import { EmptyState, ErrorState } from '../components/States';
import { SkeletonList, SkeletonTiles } from '../components/Skeleton';
import { PresenceNowState, retryPresenceNow, usePresenceMonth } from '../hooks/usePresence';
import { photoFor } from '../utils/names';
import { shortPartyName } from '../utils/party';
import { ymOf, israelToday } from '../utils/schedule';
import {
  agoLabel, durationLabel, hoursLabel, isPresenceStale, monthLabel, monthTotals, PRESENCE_FIRST_MONTH,
  PRESENCE_SOURCE, PRESENCE_START_LABEL, presenceMonths, PresenceTotals, rankingIds, sinceLabel, staySeconds,
} from '../utils/presence';

/* The במשכן tab on the Knesset page (owner's choice, 2026-10-10): who is in the Knesset
   building now, and a monthly ranking of time spent there, for sitting MKs and Norwegian
   Law ministers. Data: meta/presenceNow and presenceMonths/{YYYY-MM} (syncPresence, from
   the Knesset's own presence board). State lives in the URL: view, pFilter, pSort, pDir,
   pMonth. */

export interface PresencePerson extends MemberIndexEntry {
  partyName: string;
  partyColor: string;
  isCoalition?: boolean;
}

type View = 'now' | 'month';
type PresenceFilter = 'all' | 'coalition' | 'opposition' | 'ministers';
type PresenceMetric = 'hours' | 'days' | 'visits';
type NowOrder = 'arrival' | 'party';

const FILTERS: Array<{ key: PresenceFilter; label: string }> = [
  { key: 'all', label: 'הכול' },
  { key: 'coalition', label: 'קואליציה' },
  { key: 'opposition', label: 'אופוזיציה' },
  { key: 'ministers', label: 'שרים' },
];
const METRIC_LABEL: Record<PresenceMetric, string> = { hours: 'שעות במשכן', days: 'ימי נוכחות', visits: 'כניסות' };
const METRIC_PHRASE: Record<PresenceMetric, string> = { hours: 'שעות במשכן', days: 'ימי נוכחות', visits: 'מספר הכניסות' };
const RANK_PREVIEW = 20;

// One person on a list: from the member index, or (no member doc yet) from the presence data
interface Who {
  id: string;
  name: string;
  partyName: string;
  partyColor: string;
  isCoalition?: boolean;
  minister: boolean;
  female: boolean;
  notMk: boolean;
  role?: MemberRole;
  photo?: string;
  known: boolean;
}

const isPlainMk = (title: string) => title === 'חבר/ת כנסת' || title === 'חבר כנסת' || title === 'חברת כנסת';

const fromPerson = (p: PresencePerson): Who => ({
  id: p.id,
  name: p.name,
  partyName: p.partyName,
  partyColor: p.partyColor,
  isCoalition: p.isCoalition,
  minister: !!p.governmentMember,
  female: p.gender === 'נקבה' || (p.gender as string) === 'female',
  notMk: p.isActiveMk === false,
  role: (p.roles || []).find(r => r.isPrimary && !isPlainMk(r.title)),
  photo: photoFor(p, 40),
  known: true,
});

const FILTER_TEST: Record<PresenceFilter, (w: Who) => boolean> = {
  all: () => true,
  coalition: w => w.isCoalition === true,
  opposition: w => w.isCoalition === false,
  ministers: w => w.minister,
};

interface PresenceTabProps {
  people: PresencePerson[];
  presence: PresenceNowState;
  searchParams: URLSearchParams;
  updateParams: (updates: Record<string, string | null>) => void;
}

const PresenceTab = ({ people, presence, searchParams, updateParams }: PresenceTabProps) => {
  const { isDesktop } = useBreakpoint();
  const view: View = searchParams.get('view') === 'month' ? 'month' : 'now';
  const filterParam = searchParams.get('pFilter') as PresenceFilter | null;
  const filter: PresenceFilter = filterParam && FILTERS.some(f => f.key === filterParam) ? filterParam : 'all';

  const byId: Record<string, PresencePerson> = {};
  people.forEach(p => { byId[p.id] = p; });
  const now = presence.now;
  const whoFor = (id: string, month?: PresenceMonth | null): Who => {
    const p = byId[id];
    if (p) return fromPerson(p);
    const unknown = now && now.unknown ? now.unknown[id] : undefined;
    const name = (unknown && unknown.name) || (month && month.names ? month.names[id] : '') || 'לא ידוע';
    return {
      id, name, partyName: unknown && unknown.faction ? shortPartyName(unknown.faction) : '', partyColor: color.border,
      minister: false, female: false, notMk: !!unknown && unknown.isMk === false, known: false,
    };
  };

  const pickFilter = (k: PresenceFilter) => updateParams({ pFilter: k === 'all' ? null : k });

  const switcher = (
    <TabBar
      variant="pills"
      label="תצוגת הנוכחות"
      tabs={[{ key: 'now', label: 'עכשיו' }, { key: 'month', label: 'דירוג חודשי' }]}
      active={view}
      onChange={v => updateParams({ view: v === 'now' ? null : v })}
    />
  );
  const footnote = (
    <div style={{ fontSize: font.caption, color: color.textTertiary, lineHeight: 1.5 }}>
      {PRESENCE_SOURCE}, שמעדכנים הסדרנים בכניסות. כל כניסה ויציאה מדויקות בכדקה; שהות של יותר מ-20 שעות נספרת כיציאה שלא נרשמה.
    </div>
  );
  const frame: Frame = (side, main) => (isDesktop
    ? <TwoPane sideWidth={300} side={<Column>{switcher}{side}{footnote}</Column>}><Column>{main}</Column></TwoPane>
    : <Column>{switcher}{side}{main}{footnote}</Column>);

  return view === 'now'
    ? <NowView frame={frame} now={now} error={presence.error} clock={presence.clock} whoFor={whoFor} filter={filter} pickFilter={pickFilter}
        order={searchParams.get('pOrder') === 'party' ? 'party' : 'arrival'} setOrder={o => updateParams({ pOrder: o === 'arrival' ? null : o })}
        blocsKnown={people.some(p => p.isCoalition === true)} />
    : <MonthView frame={frame} people={people} presence={presence} whoFor={whoFor} filter={filter} pickFilter={pickFilter}
        searchParams={searchParams} updateParams={updateParams} />;
};

/* ── Layout ── */

// Each view splits itself into the summary and filters ('side') and the list ('main'). Desktop
// puts the view switch, the side blocks and the footnote in a sticky side column with the list
// beside it, like the legislation page; phone and tablet keep one column in the same order.
type Frame = (side: React.ReactNode, main: React.ReactNode) => React.ReactElement;

const Column = ({ children }: { children: React.ReactNode }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: space.md }}>{children}</div>
);

/* ── Shared bits ── */

const FilterChips = ({ counts, filter, onFilter }: { counts: Record<PresenceFilter, number>; filter: PresenceFilter; onFilter: (k: PresenceFilter) => void }) => (
  <div role="group" aria-label="סינון" style={{ display: 'flex', flexWrap: 'wrap', columnGap: space.xs + 2 }}>
    {FILTERS.filter(f => f.key === 'all' || f.key === filter || counts[f.key] > 0).map(f => (
      <Chip key={f.key} size="sm" variant="flat" selected={filter === f.key} onClick={() => onFilter(f.key)} count={counts[f.key]}>{f.label}</Chip>
    ))}
  </div>
);

const countBy = (list: Who[]): Record<PresenceFilter, number> => ({
  all: list.length,
  coalition: list.filter(FILTER_TEST.coalition).length,
  opposition: list.filter(FILTER_TEST.opposition).length,
  ministers: list.filter(FILTER_TEST.ministers).length,
});

const WhoTitle = ({ w }: { w: Who }) => (
  <span style={{ display: 'flex', alignItems: 'center', gap: space.sm, flexWrap: 'wrap' }}>
    {w.name}
    {w.minister && <Badge tone="info">{w.female ? 'שרה' : 'שר'}</Badge>}
    {w.notMk && w.minister && <span title="ויתר/ה על המושב בכנסת לפי החוק הנורווגי"><Badge>נורווגי</Badge></span>}
  </span>
);

const WhoTags = ({ w }: { w: Who }) => (
  (w.role || w.partyName) ? (
    <span style={{ display: 'flex', alignItems: 'center', gap: space.xs, flexWrap: 'wrap' }}>
      {w.role && <RoleTag role={w.role} size="small" />}
      {w.partyName && <Badge dot={w.partyColor}>{w.partyName}</Badge>}
    </span>
  ) : null
);

// Rows in one card on phones; on desktop, cards in as many columns of minColWidth+ as fit.
// Now: 320, two beside the side column at 1440px. Ranking: 340, so its fuller rows stay on one
// line (one column at 1440px, two from 1600px).
const Rows = ({ items, minColWidth = 320 }: { items: Array<(first: boolean, inGrid: boolean) => React.ReactNode>; minColWidth?: number }) => {
  const { isDesktop } = useBreakpoint();
  return isDesktop
    ? <Grid onlyOnDesktop minColWidth={minColWidth} gap="10px">{items.map((render, i) => <Card key={i} padding={0}>{render(true, true)}</Card>)}</Grid>
    : <Card padding={0}>{items.map((render, i) => <React.Fragment key={i}>{render(i === 0, false)}</React.Fragment>)}</Card>;
};

/* ── Now ── */

interface NowViewProps {
  frame: Frame;
  now: PresenceNow | null | undefined;
  error: boolean;
  clock: number;
  whoFor: (id: string) => Who;
  filter: PresenceFilter;
  pickFilter: (k: PresenceFilter) => void;
  order: NowOrder;
  setOrder: (o: NowOrder) => void;
  // False before a coalition exists: no coalition/opposition split to show
  blocsKnown: boolean;
}

const NowView = ({ frame, now, error, clock, whoFor, filter, pickFilter, order, setOrder, blocsKnown }: NowViewProps) => {
  if (now === undefined) {
    return frame(null, error
      ? <Card padding={0}><ErrorState onRetry={retryPresenceNow} /></Card>
      : <div data-skeleton="true" style={{ display: 'flex', flexDirection: 'column', gap: space.md }}><SkeletonTiles count={3} /><SkeletonList rows={6} avatar /></div>);
  }
  if (now === null) return frame(null, <Card padding={0}><EmptyState icon="users" title="אין עדיין נתוני נוכחות" /></Card>);

  const stale = isPresenceStale(now, clock);
  const everyone = Object.keys(now.present).map(id => ({ who: whoFor(id), since: now.present[id] }));
  const counts = countBy(everyone.map(x => x.who));
  const mks = everyone.filter(x => !x.who.notMk).length;
  const nonMkMinisters = everyone.filter(x => x.who.notMk && x.who.minister).length;
  const list = everyone
    .filter(x => FILTER_TEST[filter](x.who))
    .sort((a, b) => Date.parse(b.since) - Date.parse(a.since) || a.who.name.localeCompare(b.who.name, 'he'));
  const updated = agoLabel(now.polledAt, clock);

  const row = (x: { who: Who; since: string }) => (first: boolean, inGrid: boolean) => (
    <ListRow
      divider={!first && !inGrid}
      to={x.who.known ? `/members/${x.who.id}` : undefined}
      state={{ from: 'במשכן' }}
      leading={<Avatar name={x.who.name} src={x.who.photo} size={40} />}
      title={<WhoTitle w={x.who} />}
      subtitle={order === 'arrival' ? <WhoTags w={x.who} /> : (x.who.role ? <RoleTag role={x.who.role} size="small" /> : undefined)}
      trailing={(
        <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', flexShrink: 0, gap: 1 }}>
          <span style={{ fontSize: font.small, fontWeight: weight.medium, color: color.text, whiteSpace: 'nowrap' }}>מאז {sinceLabel(x.since)}</span>
          <span style={{ fontSize: font.caption, color: color.textTertiary, whiteSpace: 'nowrap' }}>{durationLabel(staySeconds(x.since, clock))}</span>
        </span>
      )}
    />
  );

  // By party: coalition parties first, then the biggest groups
  type Group = { key: string; label: string; swatch: string; isCoalition?: boolean; rows: Array<{ who: Who; since: string }> };
  const groups: Group[] = [];
  if (order === 'party') {
    const byParty: Record<string, Group> = {};
    list.forEach(x => {
      const key = x.who.partyName || 'ללא סיעה';
      if (!byParty[key]) { byParty[key] = { key, label: key, swatch: x.who.partyColor, isCoalition: x.who.isCoalition, rows: [] }; groups.push(byParty[key]); }
      byParty[key].rows.push(x);
    });
    groups.sort((a, b) => ((a.isCoalition ? 0 : 1) - (b.isCoalition ? 0 : 1)) || b.rows.length - a.rows.length);
  }

  // Side: the summary and the filters. Main: the list
  return frame(
    <>
      {/* Nobody inside and the data is fresh: the empty state below says it all */}
      {(everyone.length > 0 || stale) && (
        <Card>
          {stale && (
            <div role="status" style={{ display: 'flex', alignItems: 'flex-start', gap: space.sm, padding: `${space.sm + 2}px ${space.md}px`, marginBottom: space.md, borderRadius: radius.md, background: tone.warning.bg, color: tone.warning.fg, fontSize: font.small, lineHeight: 1.45 }}>
              <Icon name="clock" size={18} />
              <span>{now.polledAt ? `הנתונים לא התעדכנו מאז ${sinceLabel(now.polledAt)} (${updated}), כך שייתכן שמישהו כבר יצא או נכנס.` : 'הנתונים לא התעדכנו לאחרונה.'}</span>
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: blocsKnown ? 'space-between' : 'center', alignItems: 'center', textAlign: 'center', gap: space.sm, opacity: stale ? 0.6 : 1 }}>
            {blocsKnown && (
              <div>
                <div style={{ fontSize: 30, fontWeight: weight.bold, color: color.coalition, lineHeight: 1.1 }}>{counts.coalition}</div>
                <div style={{ fontSize: font.small, color: color.textSecondary }}>קואליציה</div>
              </div>
            )}
            <div style={{ minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: space.xs + 2, fontSize: font.heading, fontWeight: weight.bold, color: color.text }}>
                {!stale && everyone.length > 0 && <span aria-hidden="true" style={{ width: 9, height: 9, borderRadius: '50%', background: color.present, flexShrink: 0 }} />}
                {stale && now.polledAt ? `${everyone.length} במשכן ב-${sinceLabel(now.polledAt)}` : `${everyone.length} במשכן עכשיו`}
              </div>
              {everyone.length > 0 && (
                <div style={{ fontSize: font.small, color: color.textSecondary }}>
                  {[`${mks} ח״כים`, nonMkMinisters > 0 ? `${nonMkMinisters} ${nonMkMinisters === 1 ? 'שר שאינו ח״כ' : 'שרים שאינם ח״כים'}` : ''].filter(Boolean).join(' · ')}
                </div>
              )}
            </div>
            {blocsKnown && (
              <div>
                <div style={{ fontSize: 30, fontWeight: weight.bold, color: color.oppositionText, lineHeight: 1.1 }}>{counts.opposition}</div>
                <div style={{ fontSize: font.small, color: color.textSecondary }}>אופוזיציה</div>
              </div>
            )}
          </div>
          {everyone.length > 0 && blocsKnown && (
            <div style={{ marginTop: space.md, opacity: stale ? 0.6 : 1 }}>
              <SegmentBar
                height={10}
                label={`${counts.coalition} מהקואליציה ו-${counts.opposition} מהאופוזיציה`}
                segments={[
                  { key: 'c', value: counts.coalition, color: color.coalition },
                  { key: 'o', value: counts.opposition, color: color.opposition },
                  { key: 'u', value: everyone.length - counts.coalition - counts.opposition, color: color.border },
                ]}
              />
            </div>
          )}
          {!stale && updated && <div style={{ fontSize: font.caption, color: color.textTertiary, marginTop: space.md }}>עודכן {updated} · {PRESENCE_SOURCE}</div>}
        </Card>
      )}
      {everyone.length > 0 && <FilterChips counts={counts} filter={filter} onFilter={pickFilter} />}
    </>,
    <>
      {everyone.length === 0
        ? (
          <Card padding={0}>
            <EmptyState
              icon="users"
              title={stale ? 'לא היו חברי כנסת במשכן בעדכון האחרון' : 'אין כרגע חברי כנסת במשכן'}
              body={(
                <>
                  הרשימה מתעדכנת כל דקה, וחברי כנסת ושרים יופיעו כאן כשייכנסו לבניין.
                  {!stale && updated && <span style={{ display: 'block', marginTop: space.xs, fontSize: font.caption, color: color.textTertiary }}>עודכן {updated}</span>}
                </>
              )}
            />
          </Card>
        )
        : (
          <>
            <ResultBar
              style={{ margin: `-${space.xs}px 0` }}
              action={<TabBar compact label="סדר הרשימה" tabs={[{ key: 'arrival', label: 'לפי הגעה' }, { key: 'party', label: 'לפי סיעה' }]} active={order} onChange={setOrder} />}
            >
              {`${list.length} במשכן · ${order === 'party' ? 'לפי סיעה' : 'לפי שעת הגעה'}`}
            </ResultBar>
            {list.length === 0 && <Card padding={0}><EmptyState icon="users" title="אין תוצאות" /></Card>}
            {list.length > 0 && order === 'arrival' && <Rows items={list.map(row)} />}
            {list.length > 0 && order === 'party' && groups.map(g => (
              <section key={g.key} style={{ display: 'flex', flexDirection: 'column', gap: space.xs }}>
                <h3 style={{ margin: 0, minHeight: 32, display: 'flex', alignItems: 'center', gap: space.sm, fontSize: font.label, fontWeight: weight.bold, color: color.text }}>
                  <span aria-hidden="true" style={{ width: 10, height: 10, borderRadius: 3, background: g.swatch || color.border, flexShrink: 0 }} />
                  {g.label}
                  <span style={{ fontSize: font.small, fontWeight: weight.regular, color: color.textSecondary }}>
                    {g.rows.length}{g.isCoalition === true ? ' · קואליציה' : g.isCoalition === false ? ' · אופוזיציה' : ''}
                  </span>
                </h3>
                <Rows items={g.rows.map(row)} />
              </section>
            ))}
          </>
        )}
    </>,
  );
};

/* ── Monthly ranking ── */

interface MonthViewProps {
  frame: Frame;
  people: PresencePerson[];
  presence: PresenceNowState;
  whoFor: (id: string, month?: PresenceMonth | null) => Who;
  filter: PresenceFilter;
  pickFilter: (k: PresenceFilter) => void;
  searchParams: URLSearchParams;
  updateParams: (updates: Record<string, string | null>) => void;
}

const MonthView = ({ frame, people, presence, whoFor, filter, pickFilter, searchParams, updateParams }: MonthViewProps) => {
  const [showAll, setShowAll] = useState(false);
  const months = presenceMonths();
  const monthParam = searchParams.get('pMonth');
  const ym = monthParam && months.indexOf(monthParam) >= 0 ? monthParam : months[months.length - 1];
  const sortParam = searchParams.get('pSort') as PresenceMetric | null;
  const sort: PresenceMetric = sortParam === 'days' || sortParam === 'visits' ? sortParam : 'hours';
  const dir: 'asc' | 'desc' = searchParams.get('pDir') === 'asc' ? 'asc' : 'desc';
  const now = presence.now;
  const { month, error, retry } = usePresenceMonth(ym, (now && now.changedAt) || '');

  const isCurrent = ym === ymOf(israelToday());
  const fresh = !!now && !isPresenceStale(now, presence.clock);
  const i = months.indexOf(ym);
  const goTo = (target: string) => { setShowAll(false); updateParams({ pMonth: target === months[months.length - 1] ? null : target }); };

  const stepper = (
    <Card padding={space.md}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: space.sm }}>
        <MonthButton icon="chevronBack" label="החודש הקודם" disabled={i <= 0} onClick={() => goTo(months[i - 1])} />
        <div style={{ textAlign: 'center', minWidth: 0 }}>
          <div style={{ fontSize: font.heading, fontWeight: weight.bold, color: color.text }}>{monthLabel(ym)}</div>
          <div style={{ fontSize: font.caption, color: color.textTertiary }}>
            {ym === PRESENCE_FIRST_MONTH ? `חודש חלקי · ${PRESENCE_START_LABEL}` : isCurrent ? 'החודש, עד עכשיו' : 'כל החודש'}
          </div>
        </div>
        <MonthButton icon="chevronForward" label="החודש הבא" disabled={i >= months.length - 1} onClick={() => goTo(months[i + 1])} />
      </div>
    </Card>
  );

  if (month === undefined) {
    return frame(stepper, error
      ? <Card padding={0}><ErrorState onRetry={retry} /></Card>
      : <div data-skeleton="true" style={{ display: 'flex', flexDirection: 'column', gap: space.md }}><SkeletonTiles count={3} /><SkeletonList rows={8} avatar /></div>);
  }

  // Everyone who can appear: sitting MKs, Norwegian Law ministers, and anyone credited or
  // inside now who has no member doc yet
  const all = rankingIds(people.map(p => p.id), month, now || null, ym).map(id => {
    const totals: PresenceTotals = monthTotals(month, ym, now || null, presence.clock, id);
    return { who: whoFor(id, month), totals, here: isCurrent && fresh && !!now && !!now.present[id] };
  });
  const anyData = all.some(x => x.totals.seconds > 0);
  const counts = countBy(all.map(x => x.who));
  const inFilter = all.filter(x => FILTER_TEST[filter](x.who));

  const metricOf = (t: PresenceTotals): number => (sort === 'hours' ? t.seconds : sort === 'days' ? t.days : t.visits);
  const avg = (f: (t: PresenceTotals) => number) => (inFilter.length ? inFilter.reduce((s, x) => s + f(x.totals), 0) / inFilter.length : 0);
  const values: Record<PresenceMetric, React.ReactNode | null> = anyData
    ? {
      hours: hoursLabel(avg(t => t.seconds)),
      days: avg(t => t.days).toLocaleString('he-IL', { maximumFractionDigits: 1 }),
      visits: avg(t => t.visits).toLocaleString('he-IL', { maximumFractionDigits: 1 }),
    }
    : { hours: null, days: null, visits: null };

  // Rank by the metric, high to low; people with nothing this month get no rank
  const byName = (a: { who: Who }, b: { who: Who }) => a.who.name.localeCompare(b.who.name, 'he');
  const ranked = inFilter.slice().sort((a, b) => (metricOf(b.totals) - metricOf(a.totals)) || byName(a, b));
  const rankOf: Record<string, number> = {};
  ranked.forEach((x, n) => { if (metricOf(x.totals) > 0) rankOf[x.who.id] = n + 1; });
  const sorted = dir === 'desc' ? ranked : ranked.slice().sort((a, b) => (metricOf(a.totals) - metricOf(b.totals)) || byName(a, b));
  const max = Math.max(1, ...ranked.map(x => metricOf(x.totals)));
  const shown = showAll ? sorted : sorted.slice(0, RANK_PREVIEW);

  const valueText = (t: PresenceTotals) => (sort === 'hours' ? hoursLabel(t.seconds) : sort === 'days' ? `${t.days} ימים` : `${t.visits} כניסות`);
  const metaText = (t: PresenceTotals) => (sort === 'hours'
    ? `${t.days} ${t.days === 1 ? 'יום' : 'ימים'} · ${t.visits} ${t.visits === 1 ? 'כניסה' : 'כניסות'}`
    : `${hoursLabel(t.seconds)} שעות`);

  const onSort = (k: PresenceMetric) => {
    if (sort === k) updateParams({ pDir: dir === 'desc' ? 'asc' : null });
    else updateParams({ pSort: k === 'hours' ? null : k, pDir: null });
  };
  const changed = filter !== 'all' || sort !== 'hours' || dir !== 'desc';
  const noun = filter === 'ministers' ? 'שרים' : 'אנשים';

  const row = (x: { who: Who; totals: PresenceTotals; here: boolean }) => (first: boolean, inGrid: boolean) => (
    <ListRow
      divider={!first && !inGrid}
      to={x.who.known ? `/members/${x.who.id}` : undefined}
      state={{ from: 'במשכן', stat: 'presence' }}
      leading={(
        <span style={{ display: 'flex', alignItems: 'center', gap: space.sm, flexShrink: 0 }}>
          <span style={{ width: 24, textAlign: 'center', fontSize: font.small, fontWeight: weight.bold, color: color.textTertiary }}>{rankOf[x.who.id] || '—'}</span>
          <Avatar name={x.who.name} src={x.who.photo} size={40} present={x.here} />
        </span>
      )}
      title={<WhoTitle w={x.who} />}
      subtitle={(
        <span style={{ display: 'flex', flexDirection: 'column', gap: space.xs, marginTop: space.xxs }}>
          <Bar value={metricOf(x.totals)} max={max} height={5} color={x.who.isCoalition === false ? color.opposition : x.who.isCoalition ? color.coalition : color.textTertiary} />
          <span>{[x.who.partyName, metaText(x.totals)].filter(Boolean).join(' · ')}</span>
        </span>
      )}
      trailing={<span style={{ fontSize: font.body, fontWeight: weight.bold, color: color.text, flexShrink: 0, whiteSpace: 'nowrap' }}>{valueText(x.totals)}</span>}
    />
  );

  // Side: the month, and the averages with their filters. Main: the ranking
  return frame(
    <>
      {stepper}
      {anyData && (
        <MetricsCard
          title="ממוצע לאדם"
          groups={[{ key: 'all', label: '', keys: ['hours', 'days', 'visits'] as PresenceMetric[] }]}
          labels={METRIC_LABEL}
          values={values}
          sort={sort}
          dir={dir}
          onSort={onSort}
          filters={FILTERS.filter(f => f.key === 'all' || f.key === filter || counts[f.key] > 0).map(f => ({ key: f.key, label: f.label, count: counts[f.key] }))}
          filter={filter}
          onFilter={k => { setShowAll(false); pickFilter(k); }}
        />
      )}
    </>,
    !anyData
      ? (
        <Card padding={0}>
          <EmptyState
            icon="clock"
            title="עדיין אין שעות לחודש הזה"
            body={ym === PRESENCE_FIRST_MONTH
              ? 'שהות במשכן נספרת כשחבר הכנסת יוצא מהבניין. לוח הנוכחות של הכנסת לא שומר היסטוריה, ולכן הנתונים מתחילים ב-10.10.2026.'
              : 'שהות במשכן נספרת כשחבר הכנסת יוצא מהבניין.'}
          />
        </Card>
      )
      : (
        <>
          <ResultBar
            style={{ margin: `-${space.xs}px 0` }}
            action={changed ? <Button variant="ghost" onClick={() => { setShowAll(false); updateParams({ pFilter: null, pSort: null, pDir: null }); }} style={{ padding: `0 ${space.sm}px` }}>איפוס</Button> : undefined}
          >
            {`${inFilter.length} ${noun} · לפי ${METRIC_PHRASE[sort]}, ${dir === 'desc' ? 'מהגבוה לנמוך' : 'מהנמוך לגבוה'}`}
          </ResultBar>
          {isCurrent && fresh && all.some(x => x.here) && (
            <div style={{ display: 'flex', alignItems: 'center', gap: space.xs + 2, fontSize: font.caption, color: color.textSecondary, marginTop: -space.xs }}>
              <span aria-hidden="true" style={{ width: 9, height: 9, borderRadius: '50%', background: color.present, flexShrink: 0 }} />
              במשכן עכשיו; השעות כוללות את השהות הנוכחית
            </div>
          )}
          <Rows items={shown.map(row)} minColWidth={340} />
          {!showAll && sorted.length > RANK_PREVIEW && (
            <Button variant="secondary" fullWidth onClick={() => setShowAll(true)}>{`הצגת כל ה-${sorted.length}`}</Button>
          )}
        </>
      ),
  );
};

const MonthButton = ({ icon, label, disabled, onClick }: { icon: 'chevronBack' | 'chevronForward'; label: string; disabled: boolean; onClick: () => void }) => (
  <button
    type="button"
    aria-label={label}
    disabled={disabled}
    onClick={onClick}
    style={{
      ...buttonReset, width: 44, height: 44, borderRadius: radius.md, flexShrink: 0,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: color.surfaceMuted, color: disabled ? color.border : color.primary, cursor: disabled ? 'default' : 'pointer',
    }}
  >
    <Icon name={icon} size={20} />
  </button>
);

export default PresenceTab;
